import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AddOnCard } from "./AddOnCard";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { AddOnMetaType } from "lib/types/checkout";
import { errorToast } from "lib/utils/toasters";
import { useLocale } from "lib/hooks/useLocale";

export const AddOnSection: React.FC = () => {
  const { t } = useLocale();
  const {
    state: { availableAddOns, appliedCartAddOns, rawCheckoutObject, isC2P },
  } = useCheckoutContext();
  const { handleAddOnEdit } = useItemEdit();
  const [loadingAddOnId, setLoadingAddOnId] = useState<string | null>(null);

  const cartAddons = useMemo(
    () =>
      Object.values(availableAddOns)?.filter(
        (item) =>
          item?.addon_level === "CART" &&
          ["CHECKOUT", "DEFAULT"].includes(item?.ui_asset) &&
          item.addon_type !== "CART_NOTE",
      ),
    [availableAddOns],
  );

  // When add-ons can't be mutated (C2P), backend-applied add-ons must stay visible but not
  // addable/removable, so only already-applied ones are shown and the action is stripped below.
  const visibleCartAddons = useMemo(
    () =>
      cartAddons?.filter(
        (item) => !isC2P || appliedCartAddOns?.some((addOn) => addOn?.addon_id === item?.addon_id),
      ),
    [cartAddons, isC2P, appliedCartAddOns],
  );
  const handleActionFn = async (item: AddOnMetaType, value: boolean) => {
    setLoadingAddOnId(item.addon_id);
    try {
      await handleAddOnEdit({
        action: value ? "ADD" : "DELETE",
        addOnLevel: "CART",
        addOnId: item.addon_id,
        addOnPrice: item.price,
        showFullScreenLoader: true,
      });
    } catch (error) {
      errorToast(t("something_went_wrong"));
    } finally {
      setLoadingAddOnId(null);
    }
  };

  // "Add to cart by default" add-ons must attach themselves as soon as they're
  // eligible, without waiting for the customer to toggle them on.
  // Must never re-fire for an addon the customer just removed: rawCheckoutObject.cart_addons
  // keeps a `removed` entry around after a DELETE, and appliedCartAddOns (which is already
  // filtered by !removed) briefly reports the addon as "not applied" while that DELETE is in
  // flight, which this effect would otherwise read as "eligible to auto-add" and re-add it —
  // including after a remount/reload, when the in-memory autoAddedAddonIds ref has reset.
  const autoAddedAddonIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (isC2P) return;
    cartAddons
      .filter((item) => {
        if (!item?.default_selected || autoAddedAddonIds.current.has(item.addon_id)) return false;
        if (appliedCartAddOns?.some((addOn) => addOn?.addon_id === item?.addon_id)) return false;
        const rawAddonEntry = rawCheckoutObject?.cart_addons?.find(
          (rawAddon: any) => rawAddon?.addon_id === item.addon_id,
        );
        return !rawAddonEntry?.removed;
      })
      .forEach((item) => {
        autoAddedAddonIds.current.add(item.addon_id);
        handleActionFn(item, true);
      });
  }, [cartAddons, appliedCartAddOns, isC2P, rawCheckoutObject]);

  return (
    <>
      {visibleCartAddons?.length > 0 && (
        <div id="add-on-section" className="space-y-3 mt-3">
          {visibleCartAddons.map((item) => (
            <AddOnCard
              key={item?.addon_id}
              addOnData={item}
              isApplied={appliedCartAddOns?.some((addOn) => addOn?.addon_id === item?.addon_id)}
              handleAction={
                !isC2P
                  ? (value) => {
                      handleActionFn(item, value);
                    }
                  : undefined
              }
              isLoading={loadingAddOnId === item.addon_id}
            />
          ))}
        </div>
      )}
    </>
  );
};
